import { DataElementForTable } from '../schemas';
import { ModeData } from './types';

/**
 * 非モード項目の並び替え
 *
 * データに存在するが columnsNotModeSpecificOrder に含まれない列（固定列など）は
 * データの先頭順を維持して先頭に配置する。パターン適用時に左側の項目が抜け落ちるのを防ぐ。
 */
const swapNotModeColumnsOrder = (
  dataMode: ModeData,
  columnsNotModeSpecificOrder: string[]
): ModeData => {
  if (columnsNotModeSpecificOrder.length === 0) {
    return dataMode;
  }

  const orderSet = new Set(columnsNotModeSpecificOrder);
  const dataColumns = dataMode.data.columnsNotModeSpecific;

  const indexesNotInOrder: number[] = dataColumns
    .map((col, index) =>
      col.code != null && !orderSet.has(col.code) ? index : -1
    )
    .filter((index) => index !== -1);

  const indexesInOrder = columnsNotModeSpecificOrder
    .map((code) => dataColumns.findIndex((col) => col.code === code))
    .filter((index) => index !== -1);

  const orderedIndexes = [...indexesNotInOrder, ...indexesInOrder];

  const columnsNotModeSpecific = orderedIndexes.map((index) =>
    dataMode.data.columnsNotModeSpecific.at(index)
  ) as ModeData['data']['columnsNotModeSpecific'];

  return {
    ...dataMode,
    data: {
      ...dataMode.data,
      columnsNotModeSpecific,
      rows: dataMode.data.rows.map((row) => {
        const dataNotModeSpecific = orderedIndexes.map((index) =>
          row.dataNotModeSpecific.at(index)
        ) as DataElementForTable[];

        return {
          ...row,
          dataNotModeSpecific,
        };
      }),
    },
  };
};

/**
 * モード項目の並び替え
 *
 * データに存在するが columnsModeSpecificOrder に含まれない列は
 * データの先頭順を維持して先頭に配置する。
 */
const swapModeColumnsOrder = (
  dataMode: ModeData,
  columnsModeSpecificOrder: string[]
): ModeData => {
  if (columnsModeSpecificOrder.length === 0) {
    return dataMode;
  }

  const orderSet = new Set(columnsModeSpecificOrder);
  const dataColumns = dataMode.data.columnsModeSpecific;

  const indexesNotInOrder: number[] = dataColumns
    .map((col, index) =>
      col.code != null && !orderSet.has(col.code) ? index : -1
    )
    .filter((index) => index !== -1);

  const indexesInOrder = columnsModeSpecificOrder
    .map((code) => dataColumns.findIndex((col) => col.code === code))
    .filter((index) => index !== -1);

  const indexes = [...indexesNotInOrder, ...indexesInOrder];

  const columnsModeSpecific = indexes.map((index) =>
    dataMode.data.columnsModeSpecific.at(index)
  ) as ModeData['data']['columnsModeSpecific'];

  return {
    ...dataMode,
    data: {
      ...dataMode.data,
      columnsModeSpecific,
      rows: dataMode.data.rows.map((row) => {
        const dataModeSpecific = row.dataModeSpecific.map((data) =>
          indexes.map((index) => data.at(index))
        ) as DataElementForTable[][];

        return {
          ...row,
          dataModeSpecific,
        };
      }),
    },
  };
};

/**
 * 表示項目並び替え
 */
export const dataSwapColumnsOrder = (
  dataMode: ModeData,
  columnsNotModeSpecificOrder: string[],
  columnsModeSpecificOrder: string[]
): ModeData => {
  if (
    columnsNotModeSpecificOrder.length === 0 &&
    columnsModeSpecificOrder.length === 0
  ) {
    return dataMode;
  }

  const swappedNotMode = swapNotModeColumnsOrder(
    dataMode,
    columnsNotModeSpecificOrder
  );
  const swappedMode = swapModeColumnsOrder(
    swappedNotMode,
    columnsModeSpecificOrder
  );

  return swappedMode;
};
